import { EntraEntityType, SyncJobStatus } from '@kfolks/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { type EntraDirectoryFilters, type EntraDirectoryQueryParams, peopleApi } from '../api/people.api'
import { peopleKeys } from '../hooks/query-keys'

const ENTITY_TYPE_COLORS: Record<string, string> = {
	[EntraEntityType.USER]: 'bg-blue-100 text-blue-800',
	[EntraEntityType.GUEST]: 'bg-purple-100 text-purple-800',
	[EntraEntityType.GROUP]: 'bg-green-100 text-green-800',
	[EntraEntityType.DISTRIBUTION_LIST]: 'bg-yellow-100 text-yellow-800',
	[EntraEntityType.SHARED_MAILBOX]: 'bg-orange-100 text-orange-800',
	[EntraEntityType.ROOM]: 'bg-teal-100 text-teal-800',
	[EntraEntityType.EQUIPMENT]: 'bg-muted text-foreground',
	[EntraEntityType.UNKNOWN]: 'bg-muted text-muted-foreground',
}

const STATUS_COLORS: Record<string, string> = {
	active: 'bg-green-100 text-green-800',
	disabled: 'bg-red-100 text-red-800',
	pending_deletion: 'bg-yellow-100 text-yellow-800',
}

// Microsoft 365 license SKU IDs → friendly names
// See: https://learn.microsoft.com/en-us/entra/identity/users/licensing-service-plan-reference
const LICENSE_NAMES: Record<string, string> = {
	// Microsoft 365
	'05e9a617-0261-4cee-bb44-138d3ef5d965': 'Microsoft 365 E3',
	'06ebc4ee-1bb5-47dd-8120-11324bc54e06': 'Microsoft 365 E5',
	'44575883-256e-4a79-9da4-ebe9acabe2b2': 'Microsoft 365 F1',
	'3b555118-da6a-4418-894f-7df1e2096870': 'Microsoft 365 F3',
	'cbdc14ab-d96c-4c30-b9f4-6ada7cdc1d46': 'Microsoft 365 Business Premium',
	'f245ecc8-75af-4f8e-b61f-27d8114de5f3': 'Microsoft 365 Business Basic',
	'ac5cef5d-921b-4f97-9ef3-c99076e5571f': 'Microsoft 365 Business Standard',
	'cdd28e44-67e3-425e-be4c-737fab2899d3': 'Microsoft 365 Apps for Business',
	'c2273bd0-dff7-4215-9ef5-2c7bcfb06425': 'Microsoft 365 Apps for Enterprise',
	'2b3b8d2d-10aa-4be4-b5fd-7f2feb0c3091': 'Microsoft 365 E3 (no Teams)',
	'a640b1b8-1780-4d0d-8d48-5ffd4294e6d2': 'Microsoft 365 E5 (no Audio)',
	// Office 365
	'18181a46-0d4e-45cd-891e-60aabd171b4e': 'Office 365 E1',
	'6fd2c87f-b296-42f0-b197-1e91e994b900': 'Office 365 E3',
	'c7df2760-2c81-4ef7-b578-5b5392b571df': 'Office 365 E5',
	'4b585984-651b-448a-9e53-3b10f069cf7f': 'Office 365 F3',
	// Exchange Online
	'19ec0d23-8335-4cbd-94ac-6050e30712fa': 'Exchange Online (Plan 1)',
	'4ef96642-f517-4cd1-b2ba-aaf363fc9d1b': 'Exchange Online (Plan 2)',
	'80b2d799-d2ba-4d2a-8842-fb0d0f3a4b82': 'Exchange Online Kiosk',
	'ee02fd1b-340e-4a4b-b355-4a514e4c8943': 'Exchange Online Archiving',
	'7fc0182e-d107-4556-8329-7caaa511197b': 'Exchange Online (Plan 1)',
	// Azure AD / Entra ID
	'078d2b04-f1bd-4111-bbd4-b4b1b354cef4': 'Azure AD Premium P1',
	'84a661c4-e949-4bd2-a560-ed7766fcaf2b': 'Azure AD Premium P2',
	// EMS (Enterprise Mobility + Security)
	'efccb6f7-5641-4e0e-bd10-b4976e1bf68e': 'EMS E3',
	'b05e124f-c7cc-45a0-a6aa-8cf78c946968': 'EMS E5',
	// Power BI
	'a403ebcc-fae0-4ca2-8c8c-7a907fd6c235': 'Power BI Pro',
	'f8a1db68-be16-40ed-86d5-cb42ce701560': 'Power BI Premium Per User',
	'45bc2c81-6072-436a-9b0b-3b12eefbc402': 'Power BI Free',
	// Power Platform
	'f30db892-07e9-47e9-837c-80727f46fd3d': 'Power Automate Free',
	'dcb1a3ae-b33f-4487-846a-a640262fadf4': 'Power Apps Premium',
	'e0dfc8b9-9531-4571-873d-6e9e835afc6e': 'Power Apps Plan 2 Trial',
	'57ff2da0-773e-42df-b900-5a8840685d50': 'Power Apps per User Plan',
	'92f7a6f3-b89b-4c43-8c3f-baa2ed6b2a09': 'Power Automate per User Plan',
	'795f6fe0-cc4d-4773-b050-5c8e858c3cef': 'Power Automate Premium',
	'e2767c25-edde-406d-a2cb-1696a839deaf': 'Power Virtual Agents Viral',
	// Project
	'1f2f344a-700d-42c9-9427-5cea45d7c947': 'Project Plan 3',
	'09015f9f-377f-4538-bbb5-f75ceb09358a': 'Project Plan 5',
	'beb6439c-caad-48d3-bf46-0c82871e12be': 'Project Plan 1',
	'53818b1b-4a27-454b-8896-0dba576410e6': 'Project Online Essentials',
	// Visio
	'c5928f49-12ba-48f7-ada3-0d743a3601d5': 'Visio Plan 2',
	'4016f256-b063-4864-816e-d818aad600c9': 'Visio Plan 1',
	// Teams
	'57afa998-30b2-46cf-87d7-2bb7f0846eff': 'Microsoft Teams Essentials',
	'710779e8-3d4a-4c88-adb9-386c958d1fdf': 'Microsoft Teams Exploratory',
	'4fb214cb-a430-4a91-9c91-4a9c65199882': 'Microsoft Teams Phone Standard',
	'440eaaa8-b3e0-484b-a8be-62870b9ba70a': 'Microsoft Teams Phone Resource Account',
	// Defender / Security
	'111046dd-295b-4d6d-9724-d52ac90bd1f2': 'Microsoft Defender for Office 365 (Plan 1)',
	'8e0c0a52-6a6c-4d40-8370-dd62790dcd70': 'Microsoft Defender for Office 365 (Plan 2)',
	'26124093-3d78-432b-b5dc-48bf992543d5': 'Microsoft Defender for Endpoint P1',
	'1f6f2205-8119-43fe-a1fe-f2227dbc3484': 'Microsoft Defender for Endpoint P2',
	'906af65a-2970-46d5-9b58-4e9aa50f0657': 'Microsoft Defender for Identity',
	// Intune
	'061f9ace-7d42-4136-88ac-31dc755f143f': 'Microsoft Intune Plan 1',
	'2c21e77a-e0d6-4570-b38a-7ff2dc17d2ca': 'Microsoft Intune Plan 2',
	// SharePoint
	'a9732ec9-17d9-494c-a51c-d6b45b384dcb': 'SharePoint Online (Plan 1)',
	'6c6042f5-6f01-4d67-b8c1-eb99d36eed3e': 'SharePoint Online (Plan 2)',
	// Dynamics 365
	'ea126fc5-a19e-42e2-a731-da9d437bffcf': 'Dynamics 365 Customer Service Enterprise',
	'3a256102-e223-47ac-9c3d-57e0a9b28706': 'Dynamics 365 Sales Enterprise',
	// Copilot
	'639dec6b-bb19-468b-871c-c5c441c4b0cb': 'Microsoft 365 Copilot',
	// Azure
	'47794cd0-f0e5-45c5-9033-2eb6b5fc84e0': 'Azure Information Protection P1',
	'c52ea49f-fe5d-4f95-a0fe-f37d4530d09a': 'Azure Rights Management',
	// Other common
	'61e6bd70-fbdb-4deb-82ea-912842f39571': 'Dynamics 365 Team Members',
	'1a585bba-1ce3-416e-b1d6-9c482b52fcf6': 'Microsoft Stream',
	'a4585165-0533-458a-97e3-c400570268c4': 'Microsoft 365 Audio Conferencing',
	'e43b5b99-8dfb-405f-9987-dc307f34bcbd': 'Microsoft 365 Phone System',
	'295a8eb0-f78d-45c7-8b5b-1eed5ed02dff': 'Microsoft 365 Domestic Calling Plan',
	'fcecd1f9-a91e-488d-a918-a96cdb6ce2b0': 'Microsoft Dynamics CRM Online Basic',
	'bc946dac-7571-4167-afd1-1a4e2e0aafed': 'Power Pages Authenticated Users T1',
	'c1d032e0-5619-4761-9b5c-75b6831e1711': 'Power BI Premium Per User',
	'4b9405b0-7788-4568-add1-99614e613b69': 'Exchange Online (Plan 1)',
	// Tenant-specific SKUs (fetched via /subscribedSkus — update names as needed)
	'606b54a9-78d8-4298-ad8b-df6ef4481c80': 'Dynamics 365 P1 Trial for IT',
	'3ab6abff-666f-4424-bfb7-f0bc274ec7bc': 'Microsoft Fabric Free',
	'5b631642-bd26-49fe-bd20-1daaa972ef80': 'Microsoft Power Automate Plan 2 Trial',
	'52ea0e27-ae73-4983-a08f-13561ebdb823': 'Teams Premium (for Departments)',
	'6af4b3d6-14bb-4a2a-960c-6c902aad34f3': 'Microsoft Teams Rooms Basic',
}

function getLicenseName(skuId: string): string {
	return LICENSE_NAMES[skuId] || `Unknown (${skuId.substring(0, 8)}...)`
}

export function EntraDirectoryPage() {
	const queryClient = useQueryClient()
	const [syncJobId, setSyncJobId] = useState<string | null>(null)
	const [showSyncStatus, setShowSyncStatus] = useState(false)

	const [queryParams, setQueryParams] = useState<EntraDirectoryQueryParams>({
		page: 1,
		limit: 200,
		sort: 'createdAt',
		order: 'desc',
		filters: {},
	})

	const [tempFilters, setTempFilters] = useState<EntraDirectoryFilters>({})
	const [showLicenseDropdown, setShowLicenseDropdown] = useState(false)

	// Fetch entities
	const { data: entities, isLoading } = useQuery({
		queryKey: peopleKeys.entraDirectory.list(queryParams),
		queryFn: () => peopleApi.listEntraDirectory(queryParams),
	})

	// Sync mutation
	const syncMutation = useMutation({
		mutationFn: peopleApi.triggerEntraSync,
		onSuccess: (data) => {
			setSyncJobId(data.jobId)
			setShowSyncStatus(true)
		},
	})

	// Poll sync job status
	const { data: syncJobStatus } = useQuery({
		queryKey: peopleKeys.entraSyncJob(syncJobId),
		queryFn: () => peopleApi.getEntraSyncStatus(syncJobId ?? ''),
		enabled: !!syncJobId && showSyncStatus,
		refetchInterval: (query) => {
			const status = query.state.data?.status
			if (status === SyncJobStatus.ACCEPTED || status === SyncJobStatus.RUNNING) return 2000
			return false
		},
	})

	// Auto-refresh when sync completes
	if (
		syncJobStatus &&
		(syncJobStatus.status === SyncJobStatus.COMPLETED || syncJobStatus.status === SyncJobStatus.FAILED)
	) {
		if (syncJobStatus.status === SyncJobStatus.COMPLETED) {
			void queryClient.invalidateQueries({ queryKey: peopleKeys.entraDirectory.all })
		}
	}

	// Fetch all distinct license SKU IDs from the database (not just current page)
	const { data: allLicenseSkuIds } = useQuery({
		queryKey: peopleKeys.entraLicenses(),
		queryFn: peopleApi.listEntraLicenses,
		staleTime: 5 * 60 * 1000,
		retry: false,
	})

	// Use API data if available, otherwise fall back to extracting from loaded page data
	const availableLicenses = (() => {
		const skuIds =
			allLicenseSkuIds && allLicenseSkuIds.length > 0
				? allLicenseSkuIds
				: (() => {
						if (!entities?.data) return []
						const ids = new Set<string>()
						for (const entity of entities.data) {
							if (entity.assignedLicenses && Array.isArray(entity.assignedLicenses)) {
								for (const license of entity.assignedLicenses) {
									ids.add(license.skuId)
								}
							}
						}
						return Array.from(ids)
					})()
		return skuIds.slice().sort((a, b) => getLicenseName(a).localeCompare(getLicenseName(b)))
	})()

	// All filtering is now server-side
	const filteredEntities = entities?.data || []
	const totalItems = entities?.total ?? 0
	const totalPages = entities?.totalPages ?? 0

	const applyFilters = () => {
		setQueryParams((prev) => ({ ...prev, page: 1, filters: { ...tempFilters } }))
	}

	const clearFilters = () => {
		setTempFilters({})
		setShowLicenseDropdown(false)
		setQueryParams((prev) => ({ ...prev, page: 1, filters: {} }))
	}

	const handleSort = (field: string) => {
		setQueryParams((prev) => ({
			...prev,
			sort: field,
			order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc',
		}))
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h1 className="text-2xl font-bold text-foreground">Entra Directory</h1>
				<Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
					{syncMutation.isPending ? 'Starting...' : 'Sync from Entra ID'}
				</Button>
			</div>

			{/* Sync Status Card */}
			{showSyncStatus && syncJobStatus && (
				<div className="bg-white rounded-lg shadow p-4">
					<div className="flex justify-between items-center">
						<div>
							<h3 className="font-medium">Sync Job Status</h3>
							<Badge
								className={
									syncJobStatus.status === SyncJobStatus.COMPLETED
										? 'bg-green-100 text-green-800'
										: syncJobStatus.status === SyncJobStatus.FAILED
											? 'bg-red-100 text-red-800'
											: 'bg-blue-100 text-blue-800'
								}
							>
								{syncJobStatus.status}
							</Badge>
							{syncJobStatus.summary && (
								<p className="text-sm text-muted-foreground mt-1">
									Created: {syncJobStatus.summary.created}, Updated: {syncJobStatus.summary.updated}, Deleted:{' '}
									{syncJobStatus.summary.deleted}
								</p>
							)}
							{syncJobStatus.error && <p className="text-sm text-red-600 mt-1">{syncJobStatus.error}</p>}
						</div>
						<Button variant="ghost" size="sm" onClick={() => setShowSyncStatus(false)}>
							Close
						</Button>
					</div>
				</div>
			)}

			{/* Filters */}
			<div
				className="bg-white rounded-lg shadow p-4 space-y-3"
				onKeyDown={(e) => {
					if (e.key === 'Enter') applyFilters()
				}}
			>
				<div className="grid grid-cols-6 gap-3">
					<Input
						placeholder="Display Name"
						value={tempFilters.displayName || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, displayName: e.target.value }))}
					/>
					<Input
						placeholder="Email"
						value={tempFilters.email || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, email: e.target.value }))}
					/>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.entityType || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, entityType: e.target.value }))}
					>
						<option value="">All Types</option>
						{Object.values(EntraEntityType).map((type) => (
							<option key={type} value={type}>
								{type}
							</option>
						))}
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.status || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, status: e.target.value }))}
					>
						<option value="">All Statuses</option>
						<option value="active">Active</option>
						<option value="disabled">Disabled</option>
						<option value="pending_deletion">Pending Deletion</option>
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.accountEnabled || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, accountEnabled: e.target.value }))}
					>
						<option value="">Account Status</option>
						<option value="true">Enabled</option>
						<option value="false">Disabled</option>
					</select>
					<select
						className="border rounded-md px-3 py-2 text-sm"
						value={tempFilters.hasSignedIn || ''}
						onChange={(e) => setTempFilters((prev) => ({ ...prev, hasSignedIn: e.target.value }))}
					>
						<option value="">Sign-in Activity</option>
						<option value="yes">Has signed in</option>
						<option value="no">Never signed in</option>
					</select>
				</div>
				<div className="flex gap-3 items-start">
					<div className="relative flex-1">
						<button
							type="button"
							className="w-full border rounded-md px-3 py-2 text-sm text-left bg-white flex justify-between items-center"
							onClick={() => setShowLicenseDropdown((prev) => !prev)}
						>
							<span className="truncate">
								{tempFilters.licenses && tempFilters.licenses.length > 0
									? `${tempFilters.licenses.length} license${tempFilters.licenses.length > 1 ? 's' : ''} selected`
									: 'All Licenses'}
							</span>
							<span className="ml-1 text-muted-foreground">▾</span>
						</button>
						{showLicenseDropdown && (
							<div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto min-w-[300px]">
								{availableLicenses.map((skuId) => (
									<label
										key={skuId}
										className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted text-sm cursor-pointer"
									>
										<input
											type="checkbox"
											checked={tempFilters.licenses?.includes(skuId) || false}
											onChange={(e) => {
												setTempFilters((prev) => {
													const current = prev.licenses || []
													const updated = e.target.checked ? [...current, skuId] : current.filter((id) => id !== skuId)
													return { ...prev, licenses: updated }
												})
											}}
										/>
										<span title={getLicenseName(skuId)}>{getLicenseName(skuId)}</span>
									</label>
								))}
							</div>
						)}
					</div>
					{tempFilters.licenses && tempFilters.licenses.length > 0 && (
						<div className="flex flex-wrap gap-1 flex-1">
							{tempFilters.licenses.map((skuId) => (
								<Badge
									key={skuId}
									className="bg-indigo-100 text-indigo-800 text-xs cursor-pointer"
									onClick={() => {
										setTempFilters((prev) => ({
											...prev,
											licenses: (prev.licenses || []).filter((id) => id !== skuId),
										}))
									}}
									title="Click to remove"
								>
									{getLicenseName(skuId)} ×
								</Badge>
							))}
						</div>
					)}
					<div className="flex gap-2 shrink-0">
						<Button onClick={applyFilters} size="sm">
							Filter
						</Button>
						<Button onClick={clearFilters} variant="outline" size="sm">
							Clear
						</Button>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="bg-white rounded-lg shadow overflow-auto max-h-[70vh]">
				{isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading...</div>
				) : filteredEntities.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">No entities found</div>
				) : (
					<Table>
						<TableHeader className="sticky top-0 bg-white z-10">
							<TableRow>
								<TableHead className="cursor-pointer" onClick={() => handleSort('displayName')}>
									Display Name {queryParams.sort === 'displayName' && (queryParams.order === 'asc' ? '↑' : '↓')}
								</TableHead>
								<TableHead className="cursor-pointer" onClick={() => handleSort('email')}>
									Email {queryParams.sort === 'email' && (queryParams.order === 'asc' ? '↑' : '↓')}
								</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Account</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Job Title</TableHead>
								<TableHead>Department</TableHead>
								<TableHead>Company</TableHead>
								<TableHead>Licenses</TableHead>
								<TableHead>Last Sign-In</TableHead>
								<TableHead>Synced At</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredEntities.map((entity) => (
								<TableRow key={entity.entraDirectoryId}>
									<TableCell className="font-medium">{entity.displayName}</TableCell>
									<TableCell className="text-sm text-muted-foreground">{entity.email || '-'}</TableCell>
									<TableCell>
										<Badge className={ENTITY_TYPE_COLORS[entity.entityType] || 'bg-muted'}>{entity.entityType}</Badge>
									</TableCell>
									<TableCell>
										{entity.accountEnabled ? (
											<span className="text-green-600" title="Enabled">
												●
											</span>
										) : (
											<span className="text-red-600" title="Disabled">
												●
											</span>
										)}
									</TableCell>
									<TableCell>
										<Badge className={STATUS_COLORS[entity.status] || 'bg-muted'}>{entity.status}</Badge>
									</TableCell>
									<TableCell className="text-sm">{entity.jobTitle || '-'}</TableCell>
									<TableCell className="text-sm">{entity.department || '-'}</TableCell>
									<TableCell className="text-sm">{entity.companyName || '-'}</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-1">
											{entity.assignedLicenses &&
											Array.isArray(entity.assignedLicenses) &&
											entity.assignedLicenses.length > 0 ? (
												entity.assignedLicenses.map((license) => (
													<Badge
														key={license.skuId}
														className="bg-indigo-100 text-indigo-800 text-xs cursor-default"
														title={`${getLicenseName(license.skuId)} (${license.skuId})`}
													>
														{getLicenseName(license.skuId)}
													</Badge>
												))
											) : (
												<span className="text-muted-foreground text-sm">-</span>
											)}
										</div>
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{entity.lastSignInAt ? new Date(entity.lastSignInAt).toLocaleDateString() : '-'}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{entity.syncedAt ? new Date(entity.syncedAt).toLocaleDateString() : '-'}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Pagination */}
			<div className="flex justify-between items-center">
				<div className="flex items-center gap-2">
					<span className="text-sm text-muted-foreground">Page size:</span>
					{[20, 50, 100, 200, 500].map((size) => (
						<Button
							key={size}
							variant={queryParams.limit === size ? 'default' : 'outline'}
							size="sm"
							onClick={() => setQueryParams((prev) => ({ ...prev, limit: size, page: 1 }))}
						>
							{size}
						</Button>
					))}
				</div>
				<div className="flex gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={queryParams.page <= 1}
						onClick={() => setQueryParams((prev) => ({ ...prev, page: prev.page - 1 }))}
					>
						Previous
					</Button>
					<span className="px-3 py-1 text-sm">
						Page {queryParams.page} of {totalPages} ({totalItems} total)
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={queryParams.page >= totalPages}
						onClick={() => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }))}
					>
						Next
					</Button>
				</div>
			</div>
		</div>
	)
}
